'use client'

import { useEffect, useRef } from 'react'

import { styled } from 'styled-components'

import { Breakpoints, Colors, Shadows } from '@b-stock/fe-core/design-system'

import { ActiveBar } from '@/components/listings/search/AppliedFilters'

/**
 * z-index: 20 to be under Header mobile/tablet z-index: 25
 *
 * In particular, the page is scrollable while mobile search and nav bar
 * dropdowns are open, and they should stay over the sticky filters bar.
 *
 * We should disable the page scroll when the side slide-out navs are open,
 * but this z-index below the Header prevents bugs in case we do scroll.
 */
const FILTERS_BAR_Z_INDEX = 20

const Sticky = styled.div`
  --paddingX: 1.5rem;

  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  z-index: ${FILTERS_BAR_Z_INDEX};

  @media ${Breakpoints.min.medium} {
    --paddingX: 3rem;
  }

  @media ${Breakpoints.max.medium} {
    position: sticky;
    top: 0;
    z-index: 15;

    &.stuck {
      width: calc(100% + calc(var(--paddingX) * 2));
      padding: 1.5rem var(--paddingX);
      margin: -1.5rem calc(var(--paddingX) * -1);
      background: ${Colors.Semantic.Neutral.white};
      ${Shadows.Bottom}

      ${ActiveBar} {
        display: none;
      }
    }
  }
`

const Observer = styled.div`
  height: 0.125rem;
  width: 0.125rem;
  position: absolute;
`

type FiltersTopBar = {
  children: React.ReactNode
}

const FiltersTopBar = ({ children }: FiltersTopBar) => {
  const topBarRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const header = topBarRef.current
    const target = observerRef.current

    if (header && target) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          // Invariant: IntersectionObserver always calls callback with at least one entry
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          header.classList.toggle('stuck', !entry!.isIntersecting)
        },
        {
          threshold: 0,
        }
      )

      observer.observe(target)

      return () => {
        observer.disconnect()
      }
    }
  }, [])

  return (
    <>
      <Observer ref={observerRef} />
      <Sticky ref={topBarRef} role="banner">
        {children}
      </Sticky>
    </>
  )
}

export default FiltersTopBar
